import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordInput } from "@/components/app/PasswordInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { friendlyError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      const next = params.get("next");
      nav(next || `/${u.role}`);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[hsl(var(--navy))] text-slate-100">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1590497008432-598f04441de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzaGlwcGluZyUyMHBvcnQlMjBjb250YWluZXIlMjBhZXJpYWx8ZW58MHx8fHwxNzkxMjcwOTk5fDA&ixlib=rb-4.1.0&q=85" className="absolute inset-0 w-full h-full object-cover opacity-50"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--navy))]/80 via-[hsl(var(--navy))]/60 to-[hsl(var(--navy))]"/>
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <Link to="/" className="flex items-center gap-2 text-slate-100" data-testid="login-brand-link">
            <span className="h-8 w-8 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={18}/></span>
            <span className="font-display text-xl">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
          </Link>
          <div>
            <div className="eyebrow mb-3">Welcome back</div>
            <div className="font-display text-3xl leading-tight max-w-md">Describe what you need. We&apos;ll handle the sourcing workflow.</div>
          </div>
          <div className="text-xs text-slate-400">© {new Date().getFullYear()} GlobalBuy AI</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-slate-50 text-slate-900">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="md:hidden mb-4 flex items-center gap-2">
            <span className="h-8 w-8 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={18}/></span>
            <span className="font-display text-xl text-[hsl(var(--navy))]">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
          </div>
          <div>
            <h1 className="font-display text-3xl">Sign in</h1>
            <p className="text-sm text-slate-500 mt-1">Access your sourcing workspace.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoComplete="email"/>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} testIdPrefix="login-password"/>
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="login-submit-btn">
            {loading ? "Signing in…" : <>Sign in <ArrowRight size={16}/></>}
          </button>
          <div className="text-sm text-slate-500 text-center">
            No account? <Link to="/signup" className="text-[hsl(var(--navy))] font-medium hover:underline" data-testid="login-to-signup-link">Create one</Link>
          </div>
          <div className="text-[11px] text-slate-400 text-center">Admin demo: admin@globalbuy.ai / Admin@12345</div>
        </form>
      </div>
    </div>
  );
}
